import { inject, Injectable } from '@angular/core';

import {
  BehaviorSubject,
  catchError,
  defaultIfEmpty,
  defer,
  finalize,
  map,
  Observable,
  of,
  switchMap,
  take,
  timeout,
} from 'rxjs';

import { NavController } from '@ionic/angular';
import * as Sentry from '@sentry/angular';
import { BormaDagoCsrfApiConsumerService } from '@ts/shared/api/data-access-borma-dago-csrf';
import { AuthenticationJwtState } from '@ts/shared/authentication/util-core';
import { I18nService } from '@ts/shared/i18n/util-core';
import { ToastService } from '@ts/shared/util-toast';

import { AuthJwtRenewalService } from './auth-jwt-renewal.service';
import { AuthJwtTokenStore } from './auth-jwt-token-store';
import { FCM_LOGOUT_TOKEN_FN } from './fcm-logout-token';

export interface LoginCredentials {
  identifier: string;
  password: string;
}

// Re-exported for back-compat: authentication-init.ts and other callers import
// COOKIE_IS_AUTHENTICATED from this module path.
export { COOKIE_IS_AUTHENTICATED } from './auth-jwt-token-store';

// Re-exported for back-compat: tests import classifyJwtRenewalFailureKind from
// this module path.
export { classifyJwtRenewalFailureKind } from './auth-jwt-renewal.service';

/** How long we wait for the FCM token lookup during logout. */
const LOGOUT_FCM_TOKEN_BUDGET_MS = 1500;

/**
 * Maintains and modifies user authentication state. JWT-state management is
 * delegated to {@link AuthJwtTokenStore} and renewal to
 * {@link AuthJwtRenewalService}.
 *
 * Invariant: neither of those two may import AuthenticationService. This service
 * reaches FCMService (for logout) via the FCM_LOGOUT_TOKEN_FN factory, and that
 * chain bottoms out at the token store / renewal services — importing
 * AuthenticationService from either would re-form the DI cycle the split broke.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private tokenStore = inject(AuthJwtTokenStore);
  private renewalService = inject(AuthJwtRenewalService);
  private toastService = inject(ToastService);
  private i18nService = inject(I18nService);
  private navController = inject(NavController);
  private bormaDagoCsrfApiConsumerService = inject(
    BormaDagoCsrfApiConsumerService,
  );
  private fcmLogoutTokenFn = inject(FCM_LOGOUT_TOKEN_FN, {
    optional: true,
  }) as (() => Observable<string | null>) | null;

  // ── Delegated properties (back-compat: 20+ external callers) ──────────────

  readonly authenticationState$ = this.tokenStore.state$;
  readonly isLoggedIn$ = this.tokenStore.isLoggedIn$;

  get TOKEN_NOT_VALID_ERROR_CODE() {
    return this.tokenStore.TOKEN_NOT_VALID_ERROR_CODE;
  }

  get JWT_ERROR_CODES_TO_HANDLE_MANUALLY() {
    return this.renewalService.JWT_ERROR_CODES_TO_HANDLE_MANUALLY;
  }

  /** Setter/getter pair so `service.jwtTokenUrl = '...'` in tests forwards to
   *  the renewal service. */
  get jwtTokenUrl() {
    return this.renewalService.jwtTokenUrl;
  }
  set jwtTokenUrl(v: string) {
    this.renewalService.jwtTokenUrl = v;
  }

  // ── Own state ──────────────────────────────────────────────────────────────

  logoutUrl = 'api/user/logout/logout/';

  private isLoggingOut$ = new BehaviorSubject<boolean>(false);

  /** Whether a logout POST is in flight; guards double-click re-entrancy. */
  readonly loggingOut$: Observable<boolean> = this.isLoggingOut$.asObservable();

  constructor() {
    // IoC link: when the server rejects the credential (anywhere in the app),
    // force logout. No cycle: renewalService doesn't import
    // AuthenticationService.
    this.renewalService.authenticationRejected$.subscribe(() => this.logout());
  }

  setJwtState(authenticationJwtState: AuthenticationJwtState) {
    this.tokenStore.setState(authenticationJwtState);
  }

  renewJwtToken$(): Observable<AuthenticationJwtState> {
    return this.renewalService.renew$();
  }

  /**
   * Logs out the user. The server logout POST is authoritative: it revokes the
   * SessionToken, best-effort deactivates the device registration (#9598) and
   * clears the HttpOnly session cookie. We must therefore wait for the POST to
   * confirm (2xx) before clearing state and reloading — reloading in parallel
   * aborts the in-flight request, leaving the live cookie to re-mint a JWT on
   * boot and log the user back in (#10576).
   */
  logout() {
    if (this.isLoggingOut$.getValue()) {
      return;
    }
    this.isLoggingOut$.next(true);

    const wasLoggedIn = this.tokenStore.currentState !== null;
    if (wasLoggedIn) {
      this.toastService.info$({
        message: this.i18nService.translate$('sharedAuthentication.loggedOut'),
      });
    }

    const tokenObs$: Observable<string | null> = this.fcmLogoutTokenFn
      ? defer(() => this.fcmLogoutTokenFn!()).pipe(
          take(1),
          timeout({ each: LOGOUT_FCM_TOKEN_BUDGET_MS }),
          catchError((err) => {
            try {
              Sentry.captureMessage('logout_fcm_token_lookup_failed', {
                level: 'warning',
                extra: { error_message: String(err) },
              });
            } catch {
              // sentry transport failure must never block logout
            }
            return of(null);
          }),
        )
      : of(null);

    tokenObs$
      .pipe(
        switchMap((token) =>
          this.bormaDagoCsrfApiConsumerService.post$({
            relativeUrl: this.logoutUrl,
            body: token ? { device_token: token } : {},
            isSilent: true,
          }),
        ),
        // post$ swallows all errors to EMPTY, so a failed logout neither throws
        // nor emits — it just completes. A 2xx (even a 205 with an empty body)
        // is one emission, so map(true) marks success and defaultIfEmpty(false)
        // marks the swallowed-error case. Never rely on the error callback here.
        map(() => true),
        defaultIfEmpty(false),
        finalize(() => this.isLoggingOut$.next(false)),
      )
      .subscribe((confirmed) => {
        if (!confirmed) {
          this.toastService.danger$({
            message: this.i18nService.translate$(
              'sharedAuthentication.logoutFailed',
            ),
          });
          return;
        }
        this.tokenStore.clearState();
        if (wasLoggedIn) {
          this.navController.navigateRoot(['']).then(() => {
            window.location.reload();
          });
        }
      });
  }
}
